import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useLocalObservable } from 'mobx-react-lite'
import { useRouteMatch } from 'react-router-dom'

import { NavigationLink } from '@/component/atoms/NavigationLink'
import AcademySwitch from '@/component/molecules/AcademySwitch/AcademySwitch'
import { useAcademySwitchCandidates } from '@/component/molecules/AcademySwitch/useAcademySwitchCandidates'
import HeaderUtil from '@/component/molecules/HeaderUtil'
import { getSubjectAvailability } from '@/component/molecules/SubjectSwitch/subjectAvailability'
import { hasSwitchableSubject } from '@/component/molecules/SubjectSwitch/subjectMenu'
import SubjectSwitch from '@/component/molecules/SubjectSwitch/SubjectSwitch'
import useRepository from '@/hooks/useRepository'
import {
  getLessonStudentType,
  LESSON_STUDENT_TYPE,
} from '@/page/Lesson/_components/lessonStudentType'
import { LessonGroupService } from '@/page/Lesson/_service'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { checkOnGnbNavigate } from '@/service/forceUpdate/checkOnGnbNavigate'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import SideNavigationOpenButton from '../Navigation/SideNavigation/SideNavigationOpenButton'
import AccordionOpenButton from '../StudentGroupAccordion/AccordionOpenButton'
import AcademyLogo from './AcademyLogo/AcademyLogo'
import AcademyNavLink from './AcademyNavLink/AcademyNavLink'
import S from './Header.style'

type RouteType = {
  label: React.ReactNode
  to: () => string
  matchUrl: string
  eventName?: 'GNB_대시보드_click' | 'GNB_수업준비_click' | 'GNB_수업_click' | 'GNB_관리_click'
}
const Header = () => {
  const lessonGroupService = useRepository(LessonGroupService)

  const lessonPreparationPathMatch = useRouteMatch(`${RouteName.lessonPreparation}/*`)
  const lessonPathMatch = useRouteMatch(`${RouteName.lesson}/*`)
  const lessonAccordionKey =
    getLessonStudentType(commonRepo.router.location.pathname) === LESSON_STUDENT_TYPE.guest
      ? RouteName.lessonGuestEntranceTest
      : RouteName.lesson

  const store = useLocalObservable(() => ({
    get route() {
      const b2gHomeRoute: RouteType[] = [
        {
          label: '대시보드',
          to: () => commonRepo.router.getLastLocation(RouteName.home) || RouteName.home,
          matchUrl: RouteName.home,
          eventName: 'GNB_대시보드_click',
        },
      ]

      const routeList: RouteType[] = [
        commonRepo.permission.get.prepare.access && {
          label: '수업 준비',
          to: () =>
            commonRepo.router.getLastLocation(RouteName.lessonPreparation) ||
            RouteName.lessonPreparation,
          matchUrl: RouteName.lessonPreparation,
          eventName: 'GNB_수업준비_click',
        },
        commonRepo.permission.get.lesson.access && {
          label: '수업',
          to: lessonGroupService.fallbackPath,
          matchUrl: RouteName.lesson,
          eventName: 'GNB_수업_click',
        },
        commonRepo.permission.get.manage.access && {
          label: '관리',
          to: () => commonRepo.router.getLastLocation(RouteName.academy) || RouteName.academy,
          matchUrl: RouteName.academy,
          eventName: 'GNB_관리_click',
        },
      ].filter(Boolean) as RouteType[]
      if (commonRepo.isSchoolflat) {
        return [...b2gHomeRoute, ...routeList]
      } else {
        return routeList
      }
    },
  }))

  // 설탭은 과목 전환 미제공. 그 외에는 메뉴에 띄울 과목이 하나라도 있어야 트리거를 노출한다.
  const isSubjectSwitchVisible =
    !commonRepo.isSeoltab && hasSwitchableSubject(getSubjectAvailability())

  const isAcademySwitchAllowed = !!commonRepo.permission?.byFeature.isAllowed('ACADEMY_SWITCH')
  // 여백은 트리거가 실제로 그려질 때만 좁힌다. 권한만 보면 목록을 받기 전이나 소속이 하나일 때
  // 빈 자리에 좁은 여백이 적용된다.
  const { canSwitchAcademy } = useAcademySwitchCandidates({ enabled: isAcademySwitchAllowed })

  return (
    <header className={clsx(commonRepo.isSchoolflat && 'schoolflat')} css={S.header}>
      <div className="header-container">
        <div className={clsx('header-container-left')}>
          {lessonPathMatch?.isExact && <AccordionOpenButton routeKey={lessonAccordionKey} />}
          {lessonPreparationPathMatch?.isExact && <SideNavigationOpenButton />}
          <AcademyLogo routeKey={RouteName.lesson} />
          {isAcademySwitchAllowed && <AcademySwitch />}
          {isSubjectSwitchVisible && <SubjectSwitch />}
          <nav
            className={clsx(
              'nav-link',
              canSwitchAcademy && 'nav-link__academy-switch',
              store.route.find((a) => a.label === '수업') && 'nav-link__lesson',
            )}
          >
            {store.route.map((route) => {
              const { eventName } = route
              return (
                route && (
                  <NavigationLink
                    key={route.matchUrl}
                    data-mathflat-unique-id={route.matchUrl}
                    to={route.to}
                    matchUrl={route.matchUrl}
                    onClick={() => {
                      checkOnGnbNavigate(typeof route.to === 'function' ? route.to() : route.to)
                      if (eventName) mixpanelClient.track(eventName)
                    }}
                  >
                    {route.label}
                  </NavigationLink>
                )
              )
            })}
            {commonRepo.academy?.isOnlineOnboardingActive && <AcademyNavLink />}
          </nav>
        </div>
        <div className={clsx('header-container-right')}>
          <HeaderUtil />
        </div>
      </div>
    </header>
  )
}

export default observer(Header)
